// The plan, as presented. Transcribed verbatim from ADCG_Reorganization.pptx (four slides,
// August 2026 directives).
//
// WHY THIS IS CONTENT AND NOT DATA. The deck is narrative — it states what the structure
// IS, not what it currently measures. Nothing here is computed, aggregated or reconciled,
// so putting it behind an API would add a round trip, a serializer and a migration to
// deliver prose that changes when the Shareholder changes the plan, not when the book
// moves. Live STATUS is a different matter and does come from the register: see
// PolicyPackPage, which joins these titles to the API through an explicit reference map.
//
// The deck slides are 16:9 landscape diagrams with 8pt body text. They are NOT embedded as
// images: at 375px a 2000px-wide slide renders its body copy at roughly 1.5pt, which is
// unreadable, and pinch-zooming a governance document on a phone is not a design. The
// structure below carries the same content and reflows — three columns on a desktop, one
// book at a time on a phone.
//
// Any edit here must keep the wording identical to the deck. If the deck changes, this
// changes in the same commit — a plan screen that disagrees with the document being
// presented is worse than no plan screen.

export const TONE_ORDER = ['navy', 'teal', 'gold'];

// ---------------------------------------------------------------------------
// Slide 1 — Portfolio structure
// ---------------------------------------------------------------------------

export const PORTFOLIO = {
  key: 'portfolio',
  kicker: 'Portfolio architecture',
  kickerSub: 'Three books and an overlay',
  headline: 'Portfolio Structure',
  footnote: 'The pool is what lets the core book absorb a drawdown without being forced to sell.',
  top: 'Shareholder',
  columns: [
    {
      key: 'core',
      tone: 'navy',
      title: 'Core Investment Book',
      short: 'Core',
      tagline: 'the growth engine · fully liquid, sellable within days',
      blocks: [
        {
          title: 'Active management by the team',
          bullets: [
            'Listed liquid capital market investments',
            'Liquidatable within days to weeks',
            'Daily P&L visibility',
          ],
          callout: {
            label: 'Mandate',
            text: 'Outperform the S&P 500. Alpha measured over a rolling 3-year window — 50bp over 3 years.',
          },
        },
        {
          title: 'Hedging overlay',
          bullets: [
            'Tail-risk protection beyond 20 – 25% of downside',
            'Capital preservation during market down cycles',
            'Protects against outsized, extreme market downturns such as the GFC',
          ],
          callout: { label: 'Cost', text: '100 – 125bp per annum, charged to this book' },
        },
        {
          title: 'Net return after hedging cost',
          filled: true,
          body: 'Portfolio return less hedging cost. Expected to broadly track the S&P 500, with additional downside protection through a crisis.',
        },
      ],
      governedBy: ['Core Book Investment Policy Statement'],
    },
    {
      key: 'liquidity',
      tone: 'teal',
      title: 'Liquidity Pool',
      short: 'Liquidity',
      tagline: 'the reserve · cash within days',
      blocks: [
        {
          title: 'Purpose 1 → Liquidity to the Shareholder',
          bullets: [
            'Enables any discretionary withdrawal by the Shareholder',
            'Overnight liquidity on the bulk of the pool, without advance notice',
            'Held at a set percentage of AUM at the Shareholder’s discretion',
          ],
        },
        {
          title: 'Purpose 2 → Crisis deployment',
          bullets: [
            'Can be tapped during a market crisis to buy at lower prices',
            'Periodically recycled back on recovery',
            'Deployed only after the Shareholder’s approval',
          ],
        },
        {
          title: 'Purpose 3 → Receives non-core proceeds',
          bullets: [
            'Dividends, distributions and exit proceeds from the run-off portfolio are received here',
            'The Shareholder decides on further deployment into the core book, or withdrawal',
          ],
        },
        {
          title: 'Expected composition',
          bullets: [
            'Money market, T-bills and government paper',
            'Investment grade fixed income',
            'Potentially some stable dividend-yielding stocks for recurring cash flow',
          ],
          callout: { label: 'Target', text: '5 – 6% cash flow and yield' },
        },
      ],
      governedBy: ['Income & Liquidity Book IPS', 'Liquidity Policy'],
    },
    {
      key: 'runoff',
      tone: 'gold',
      title: 'Run-off Portfolio',
      short: 'Run-off',
      tagline: 'realise and redeploy · illiquid, exits take years',
      blocks: [
        {
          title: 'Private equity',
          bullets: [
            'Actively managed; exits sought where appropriate and value-accretive',
            'Funds — held to maturity, with secondary sale attempted where valuation is justified',
            'Co-investments — held with a plan to exit alongside the sponsor as the timeline allows',
            'Principal investments — illiquid direct holdings in UAE companies; review and action plan to follow, most positions held for dividend',
            'Undrawn commitments already made continue to be funded',
          ],
          callout: {
            label: 'Next step',
            text: 'Management plan with realisation timeline and cash flow forecast',
          },
        },
        {
          title: 'RE funds and co-investments',
          bullets: [
            'Held to maturity; secondary sale attempted where valuation is justified',
            'Sponsor-led exits and distributions taken as they arise',
            'Several positions carry pledges to be released before exit',
          ],
          callout: {
            label: 'Next step',
            text: 'Management plan with realisation timeline and cash flow forecast',
          },
        },
        {
          title: 'Real estate asset management',
          bullets: [
            'Income-yielding property',
            'Retained for the rental stream, not queued for exit',
            'Rental income swept to the liquidity pool',
          ],
          callout: { label: 'Retained', text: 'Held for income · annual retention review' },
        },
      ],
      governedBy: ['Liquidation & Run-off Policy'],
    },
  ],
  // Directed, because the direction is the whole point: the pool funds the core book and
  // receives from both, and only the pool pays the Shareholder.
  //
  // `tone` is the colour of the book the money LEAVES, so an arrow can be traced back to
  // its source at a glance without reading the label.
  flows: [
    { from: 'liquidity', to: 'core', label: 'deploy', tone: 'teal' },
    { from: 'core', to: 'liquidity', label: 'return', tone: 'navy' },
    { from: 'runoff', to: 'liquidity', label: 'proceeds', tone: 'gold' },
    { from: 'liquidity', to: 'top', label: 'distributions', tone: 'teal' },
  ],
};

// ---------------------------------------------------------------------------
// Slide 2 — Operating model
// ---------------------------------------------------------------------------

export const OPERATING = {
  key: 'operating',
  kicker: 'Operating model',
  kickerSub: 'Team, platform and automation',
  headline: 'Operating Model',
  footnote: 'The core team is the capability that has already delivered. The platform and the automation are built around it.',
  columns: [
    {
      key: 'team',
      tone: 'navy',
      title: 'The Team',
      short: 'Team',
      tagline: 'the capability that delivers',
      blocks: [
        {
          title: 'Core investment team',
          bullets: [
            'Responsible for generating outperformance and constructing the portfolio',
            'Talent from top-tier institutions, with a track record at ADCG',
            'Expanded as and when required, with the Shareholder’s approval',
          ],
        },
        {
          title: 'Run-off resources',
          bullets: [
            'Dedicated resources for the private equity and real estate run-off',
            'The most effective staff from the existing team retained',
            'Chosen for capability beyond portfolio work — contributing across the platform',
          ],
        },
        {
          title: 'Asset management team',
          bullets: [
            'Manages the income-yielding assets on the ground',
            'Continues in place, reporting into the same structure',
            'Rental income and distributions swept to the liquidity pool',
          ],
        },
      ],
      governedBy: ['Hiring Policy', 'Remuneration Policy'],
    },
    {
      key: 'platform',
      tone: 'teal',
      title: 'Central Platform',
      short: 'Platform',
      tagline: 'one system, full visibility',
      blocks: [
        {
          title: 'Shareholder visibility',
          bullets: [
            'AUM, allocation, liquidity and performance, continuously available',
            'Distributable liquidity as the headline metric',
            'No reporting cycle to wait for and no request to make',
          ],
        },
        {
          title: 'Built on the systems we already run',
          bullets: [
            'Integrated with our existing Bloomberg installation — terminal, PORT and AIM',
            'Institutional-grade performance, risk and portfolio analytics taken as calculated',
            'The platform presents and consolidates; it does not re-derive what Bloomberg already produces',
          ],
        },
        {
          title: 'Approvals and audit trail',
          bullets: [
            'Team approvals and the CIO’s approval of policies recorded on the system',
            'Every decision carries its date, rationale and approver',
            'Full history retained and searchable',
          ],
        },
      ],
      governedBy: ['Dashboard & KPI Policy', 'Delegation of Authority'],
    },
    {
      key: 'automation',
      tone: 'gold',
      title: 'AI and Automation',
      short: 'AI',
      tagline: 'capability, not headcount',
      blocks: [
        {
          title: 'Research and institutional memory',
          bullets: [
            'Research workflows automated end to end',
            'Document extraction across fund papers, side letters and consents',
            'Analysis retained and searchable — knowledge stays when people move',
          ],
        },
        {
          title: 'Systematic strategies',
          bullets: [
            'Signal research, backtesting and execution automation',
            'Developed and deployed only under approved policy',
            'Human approval retained on every deployment',
          ],
        },
        {
          title: 'Back office, over time',
          bullets: [
            'Accounting, bookkeeping and reconciliation automated progressively',
            'Reduces cost per unit of assets as the book grows',
            'Built after the reporting layer, not before it',
          ],
        },
      ],
      governedBy: ['AI & Automation Policy', 'Technology Plan'],
    },
  ],
  sequence: {
    label: 'Build sequence',
    note: 'Automating a process before it is defined only produces faster errors — which is why the platform comes first and the back office comes last.',
    steps: [
      { when: 'Months 1 – 3', title: 'Initial platform', detail: 'Capital markets performance visibility, the initial policy set, and performance of automated strategies.', tone: 'teal' },
      { when: 'Months 3 – 9', title: 'Automate and optimise', detail: 'Further tasks automated: reconciliation, capital-call tracking and document extraction across the legacy book.', tone: 'navy' },
      { when: 'Months 9 – 18', title: 'Research layer', detail: 'Research workflows, institutional memory and systematic strategy development under policy.', tone: 'gold' },
      { when: 'Year 2 onward', title: 'Back-office module', detail: 'Accounting, bookkeeping and reconciliation, once the reporting layer is proven.', tone: 'gold' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Slide 3 — Governance framework (the policy pack)
// ---------------------------------------------------------------------------
//
// `ref` is the register reference this document maps to, or null where the register has no
// counterpart. The mapping is written by hand and matched exactly — never by title
// similarity. A fuzzy match here would silently attach one policy's approval status to a
// different policy, which is the one error a governance screen must not make.

export const GOVERNANCE = {
  key: 'governance',
  kicker: 'Governance framework',
  kickerSub: 'The policy pack',
  headline: 'Governance Framework',
  footnote: 'Twenty-three policies, not fifty. A framework nobody can hold in their head is one nobody follows.',
  charter: {
    title: 'Master Governance Charter',
    tagline: 'perimeter · reserved matters · delegation principles · reporting · annual certification',
    derivation: 'Derived from the Shareholder’s directives of August 2026',
    ref: 'L0-CHARTER',
  },
  meantime: 'Portfolios continue to be mandated under the existing policies until the revised set is drafted and approved by the Shareholder.',
  totalNote: 'Twenty-three documents inside four months. Ten are drafted in-house; the remainder are adapted from established templates and reviewed rather than written from scratch.',
  columns: [
    {
      key: 'investment',
      tone: 'navy',
      title: 'Investment',
      short: 'Investment',
      tagline: 'how capital is managed',
      policies: [
        { title: 'Master IPS — including risk appetite and limits', tier: 'T1', ref: 'L1-MIPS' },
        { title: 'Core Book IPS', tier: 'T1', ref: 'L2-IPS-CORE' },
        { title: 'Asset-class IPS — one per asset class', tier: 'T2', sub: true, ref: null },
        { title: 'Liquidity Pool IPS', tier: 'T1', ref: 'L2-IPS-INC' },
        { title: 'Liquidation & Run-off Policy', tier: 'T1', ref: 'L2-IPS-RUN' },
        { title: 'Hedging & Derivatives', tier: 'T2', ref: null },
        { title: 'Benchmark & Performance Measurement', tier: 'T2', ref: null },
      ],
    },
    {
      key: 'capital',
      tone: 'teal',
      title: 'Capital & Control',
      short: 'Capital',
      tagline: 'what may move, and who decides',
      policies: [
        { title: 'Delegation of Authority Framework', tier: 'T1', ref: null },
        { title: 'Capital Transfer & Payment Authority', tier: 'T1', ref: null },
        { title: 'Valuation Policy', tier: 'T1', ref: null },
        { title: 'Risk & Liquidity Measurement Framework', tier: 'T1', ref: null },
        { title: 'Days to liquidate · exposure · stress · distributable liquidity', tier: 'T1', sub: true, ref: null },
        { title: 'Borrowing, Pledge & Collateral', tier: 'T2', ref: null },
        { title: 'Counterparty, Custody & Trading', tier: 'T2', ref: null },
        { title: 'Conflicts & Related-Party', tier: 'T2', ref: null },
      ],
    },
    {
      key: 'operating',
      tone: 'gold',
      title: 'Operating & People',
      short: 'Operating',
      tagline: 'how the platform and team run',
      policies: [
        { title: 'Expense & Budget Policy', tier: 'T2', ref: null },
        { title: 'Remuneration, Bonus & LTIP', tier: 'T2', ref: null },
        { title: 'Hiring & Resourcing', tier: 'T3', ref: null },
        { title: 'Reporting & Dashboard', tier: 'T2', ref: null },
        { title: 'Technology, AI & Automation', tier: 'T3', ref: null },
        { title: 'Business Continuity & Resilience', tier: 'T3', ref: null },
        { title: 'Compliance, AML & Record-Keeping', tier: 'T3', ref: null },
      ],
    },
  ],
  implementation: {
    label: 'Implementation',
    steps: [
      { when: 'Week 1', title: 'Charter and mandate', detail: 'Governance Charter and Master IPS. The documents everything else hangs from.', tone: 'navy' },
      { when: 'Weeks 2 – 3', title: 'Investment policies', detail: 'Core Book IPS, Liquidity Pool IPS, asset-class IPS documents, hedging and benchmark.', tone: 'navy' },
      { when: 'Weeks 4 – 6', title: 'Run-off and authorities', detail: 'Liquidation and run-off policy, valuation, delegation of authority and transfer authority.', tone: 'teal' },
      { when: 'Months 2 – 4', title: 'Control, operating and platform', detail: 'Risk and liquidity measurement, borrowing, counterparty and conflicts, then the operating and people set.', tone: 'gold' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Slide 4 — Immediate next steps
// ---------------------------------------------------------------------------

export const NEXT_STEPS = {
  key: 'next',
  kicker: 'Immediate next steps',
  kickerSub: 'What we need, and what follows',
  headline: 'Immediate Next Steps',
  footnote: 'Nothing above requires capital to move. These are the steps that make the plan measurable.',
  approval: {
    label: 'Approval sought',
    title: 'Private data environment',
    body: 'Approval to push portfolio, position and performance data into a private, controlled database — the single record behind the platform, the dashboard and every figure reported to the Shareholder.',
    note: 'The dashboard will be linked to the Bloomberg reporting engine for capital markets, and to the books of record for private equity and real estate, to avoid any recalculation errors.',
  },
  sequenceLabel: 'Then, in sequence',
  steps: [
    { n: 1, when: 'Week 1', title: 'Central platform — initial version', detail: 'The plan and policy statements uploaded for Shareholder reference. Updates follow as documents are approved.', tone: 'teal' },
    { n: 2, when: 'Weeks 1 – 3', title: 'Charter, Master IPS and Tier 1 policies', detail: 'The documents that gate every subsequent decision.', tone: 'navy' },
    { n: 3, when: 'Weeks 3 – 6', title: 'Asset exposure deep dive', detail: 'Position-level view of the book: liquid, illiquid, encumbered and free.', tone: 'teal' },
    { n: 4, when: 'Weeks 4 – 8', title: 'Cash flow forecast', detail: 'Distributions, rental income and expected realisations.', tone: 'teal' },
    { n: 5, when: 'Weeks 6 – 10', title: 'Expense forecast and team structure', detail: 'Built with Group finance and submitted for approval.', tone: 'navy' },
    { n: 6, when: 'Months 2 – 4', title: 'Remaining policies', detail: 'Roughly one a week until the full set is drafted and approved.', tone: 'navy' },
    { n: 7, when: 'From month 3', title: 'Central reporting platform — build begins', detail: 'Performance, liquidity and exposure reporting, integrated with the existing Bloomberg systems.', tone: 'teal' },
    { n: 8, when: 'Ongoing', title: 'Full reorganisation', detail: 'Assets restructured into the three books, in line with the approved plan.', tone: 'gold' },
  ],
};

export const CONFIDENTIAL = 'ADCG · Strictly private and confidential';

// ---------------------------------------------------------------------------
// The section index
// ---------------------------------------------------------------------------
//
// What Project Delta contains, in the deck's own order. `blurb` is written for someone who
// has not read the deck: it says what the part covers in one sentence, in plain words,
// because a list of four titles tells you nothing about which one holds your answer.
//
// Route slugs live here too, so the navigation and the content cannot fall out of step.

// ONE LINE. It says what the plan is and stops.
//
// NEVER describe whose money this is. An earlier draft opened with "the family's
// investments" — nothing in the deck or the Charter says that, and inventing a
// characterisation of the owner in a document written FOR the owner is the worst place to
// guess. The deck's term throughout is "the Shareholder"; use that or name nothing.
export const DELTA_INTRO =
  'Restructuring the portfolio into three books under a single policy framework, reported '
  + 'on one platform. Nothing here requires capital to move.';

// Title plus one tag. No kicker line above the tag: the deck's own slide sub-headings
// ("Three books and an overlay") are a presenter's framing, and reading them back on a
// navigation card is prose where a label belongs.
export const DELTA_SECTIONS = [
  {
    slug: 'structure',
    tone: 'navy',
    title: 'Portfolio Structure',
    blurb: 'How capital is split, and how it moves.',
  },
  {
    slug: 'operating',
    tone: 'teal',
    title: 'Operating Model',
    blurb: 'Team, platform and automation.',
  },
  {
    slug: 'policies',
    tone: 'gold',
    title: 'Policy Pack',
    blurb: 'What governs each decision, and who approves it.',
  },
  {
    slug: 'execution',
    tone: 'navy',
    title: 'Execution',
    blurb: 'What is being asked for, and what follows.',
  },
];
